import {
	DeleteOutlined,
	EditOutlined,
	QuestionCircleOutlined,
} from "@ant-design/icons";
import type { InputRef } from "antd";
import {
	Button,
	Collapse,
	Flex,
	Form,
	Input,
	InputNumber,
	Switch,
	TimePicker,
	Tooltip,
	theme,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";

const handleStopPropagation = (e: React.MouseEvent) => {
	e.stopPropagation();
};

const DEFAULT_CANCEL_CONDITIONS = [
	{
		name: "状態 1",
		day: 0,
		hour: 18,
		minute: 0,
		refundPercentage: 100,
	},
];

const isValidHour = (hour: number) => hour >= 0 && hour <= 23;
const isValidMinute = (minute: number) => minute >= 0 && minute <= 59;

const LabelName = ({ name }: { name: (string | number)[] }) => {
	const form = Form.useFormInstance();
	const [isEditing, setIsEditing] = useState(false);
	const [inputValue, setInputValue] = useState(form.getFieldValue(name));
	const ref = useRef<InputRef>(null);

	const onClick = (e: React.MouseEvent) => {
		handleStopPropagation(e);
		setIsEditing(true);
		setTimeout(() => ref?.current?.focus(), 0);
	};

	const handleEditDone = () => {
		setIsEditing(false);
		form.setFieldValue(name, inputValue);
	};

	return (
		<Flex gap={15}>
			<Input
				ref={ref}
				size={"small"}
				variant={isEditing ? "filled" : "borderless"}
				readOnly={!isEditing}
				onBlur={handleEditDone}
				value={inputValue}
				onChange={(e) => setInputValue(e.target.value)}
				style={{
					cursor: !isEditing ? "pointer" : "auto",
					width: isEditing ? 200 : "auto",
				}}
				onClick={!isEditing ? onClick : undefined}
			/>

			{!isEditing && (
				<Button
					onClick={onClick}
					size="small"
					type="text"
					icon={<EditOutlined />}
				>
					編集
				</Button>
			)}
		</Flex>
	);
};

export const CategoryCancelConditions = () => {
	const form = Form.useFormInstance();
	const { token } = theme.useToken();
	const colorPrimary = token.colorPrimary;

	const conditions = Form.useWatch("cancelConditions", form);
	useEffect(() => {
		if (conditions?.length > 1) {
			form.setFieldValue("cancelConditions", [conditions[0]]);
		}
	}, [conditions, form]);

	return (
		<>
			<div className="flex justify-between items-center h-8 mb-4">
				<Flex gap={10} align="center">
					このイベントの独自キャンセル設定
					<Tooltip
						title={
							<div>
								<div>OFFの場合は[設定]ページの登録内容が踏襲されます。</div>
							</div>
						}
						overlayInnerStyle={{ width: 300 }}
					>
						<QuestionCircleOutlined
							style={{
								color: colorPrimary,
								marginRight: 9,
							}}
						/>
					</Tooltip>
					<Form.Item
						labelCol={{ span: 24 }}
						wrapperCol={{ span: 24 }}
						className="mb-0"
						name="cancelable"
						valuePropName="checked"
					>
						<Switch
							onChange={(val) => {
								form.setFieldValue("cancelable", val);
								if (!form.getFieldValue("cancelConditions")?.length) {
									form.setFieldValue(
										"cancelConditions",
										DEFAULT_CANCEL_CONDITIONS,
									);
								}
							}}
						/>
					</Form.Item>
				</Flex>
			</div>

			<Form.List name="cancelConditions">
				{(fields, { remove }) => (
					<Form.Item
						noStyle
						shouldUpdate={(prevValues, currentValues) =>
							prevValues.cancelable !== currentValues.cancelable
						}
					>
						{({ getFieldValue }) => {
							const cancelable = getFieldValue("cancelable");
							return cancelable ? (
								<div className="flex flex-col gap-4 mb-6">
									<Collapse
										size="small"
										items={fields.map((field, index) => {
											return {
												extra: (
													<Flex gap={10}>
														<Button
															size="small"
															type="text"
															icon={<DeleteOutlined />}
															danger
															onClick={(e) => {
																handleStopPropagation(e);
																remove(index);
																if (fields.length - 1 === 0) {
																	form.setFieldValue("cancelable", false);
																}
															}}
														>
															削除
														</Button>
													</Flex>
												),
												key: field.key,
												label: (
													<LabelName
														name={["cancelConditions", field.name, "name"]}
													/>
												),
												children: (
													<>
														<Form.Item
															{...field}
															name={[field.name, "day"]}
															label="日数（予約より何日前までキャンセル可能）"
															rules={[
																{
																	required: true,
																	message: "キャンセル可能日は必須です",
																},
															]}
														>
															<InputNumber
																placeholder="例：1"
																addonBefore="予約より"
																addonAfter="日前"
																min={0}
																type="number"
																onPressEnter={(e) => {
																	e.preventDefault();
																}}
															/>
														</Form.Item>
														<Form.Item
															noStyle
															shouldUpdate={(prevValues, currValues) => {
																const prev =
																	prevValues.cancelConditions?.[index];
																const curr =
																	currValues.cancelConditions?.[index];
																return (
																	`${prev?.hour},${prev?.minute}` !==
																	`${curr?.hour},${curr?.minute}`
																);
															}}
														>
															{({ getFieldValue }) => {
																const hour = getFieldValue([
																	"cancelConditions",
																	field.name,
																	"hour",
																]);
																const minute = getFieldValue([
																	"cancelConditions",
																	field.name,
																	"minute",
																]);

																const getTime = (h: number, m: number) => {
																	if (!isValidHour(h) || !isValidMinute(m)) {
																		return undefined;
																	}
																	return dayjs().hour(h).minute(m);
																};

																return (
																	<Form.Item
																		label="時間（何時何分までキャンセル可能）"
																		// Note: Antd Form Item with nested names and value handling can be tricky.
																		// We use a dummy wrapper or handle via onChange in TimePicker
																	>
																		<TimePicker
																			placeholder="時間"
																			popupClassName="hide-timepicker-footer"
																			format="HH:mm"
																			inputReadOnly
																			showNow={false}
																			allowClear={false}
																			hideDisabledOptions={true}
																			minuteStep={5}
																			style={{ width: 140 }}
																			value={getTime(hour, minute)}
																			onSelect={(time) => {
																				form.setFieldValue(
																					[
																						"cancelConditions",
																						field.name,
																						"hour",
																					],
																					+time.format("HH"),
																				);
																				form.setFieldValue(
																					[
																						"cancelConditions",
																						field.name,
																						"minute",
																					],
																					+time.format("m"),
																				);
																			}}
																		/>
																	</Form.Item>
																);
															}}
														</Form.Item>
														<Form.Item
															noStyle
															shouldUpdate={(prevValues, currentValues) =>
																prevValues.fee !== currentValues.fee
															}
														>
															{({ getFieldValue }) => {
																// Only show refund percentage if event is paid
																const fee = parseFloat(
																	getFieldValue("fee") ||
																		getFieldValue("price"),
																);
																return fee > 0 ? (
																	<Form.Item
																		name={[field.name, "refundPercentage"]}
																		hidden
																		label="キャンセル後の払い戻し率"
																		rules={[
																			{
																				required: true,
																				message: "払い戻し率は必須です",
																			},
																		]}
																	>
																		<InputNumber
																			placeholder="例：1"
																			addonAfter="%"
																			min={0}
																			max={100}
																			type="number"
																			onPressEnter={(e) => {
																				e.preventDefault();
																			}}
																			style={{ width: 140 }}
																		/>
																	</Form.Item>
																) : null;
															}}
														</Form.Item>
													</>
												),
											};
										})}
									/>
								</div>
							) : null;
						}}
					</Form.Item>
				)}
			</Form.List>

			<Form.Item
				shouldUpdate={(prevValues, currentValues) =>
					prevValues.cancelable !== currentValues.cancelable
				}
				noStyle
			>
				{({ getFieldValue }) => {
					const cancelable = getFieldValue("cancelable");

					if (!cancelable) return null;

					return (
						<Form.Item
							name="cancelDescription"
							label="キャンセルに関する説明文"
							rules={[
								{
									required: false,
									message: "キャンセルに関する説明文は必須です",
								},
							]}
							className="[&_.ant-form-item-label>label]:h-8"
						>
							<Input.TextArea
								placeholder="キャンセルに関する説明文を入力してください"
								autoSize={{ minRows: 2 }}
							/>
						</Form.Item>
					);
				}}
			</Form.Item>
		</>
	);
};
